import { createSelectorCreator, defaultMemoize } from 'reselect';
import isEqual from 'lodash.isequal';
import upperFirst from 'lodash.upperfirst';
import get from 'lodash.get';
import has from 'lodash.has';
import first from 'lodash.first';
import isEmpty from 'lodash.isempty';
import isPlainObject from 'lodash.isplainobject';

import {
  CURRENCY_NATIVE,
  CURRENCY_USD,
  DEFAULT_EXCHANGE,
  DEFAULT_MINER,
  FORMATTER_CURRENCY,
  FORMATTER_CURRENCY_USD,
  RESOLUTION_10MIN,
  RESOLUTION_1H,
  RESOLUTION_1M,
  RESOLUTION_1W,
  RESOLUTION_24H,
  STATUS_TOO_MANY_REQUESTS,
  MOVING_AVERAGE,
  MOVING_MEDIAN,
} from '../../constants/app';
import { METRICS_OPTIONS, METRICS_OPTIONS_PATH_KEYD } from '../../constants/catalog';

import * as Content from '../../attrs/content';

import { ma } from '../../utils/movingAverage';
import { mm } from '../../utils/movingMedian';

export const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

export const selectIdxProp = (_, props) => props.idx;

export const selectAssetProp = (_, props) => props.asset;

export const getMetricConfig = (code) => get(METRICS_OPTIONS, code, {});

export const getMetricConfigByPath = (path) => get(METRICS_OPTIONS_PATH_KEYD, path, {});

export const getAvailableAssets = (assets) => {
  const topAssets = assets.filter((a) => !a.isERC20).map((f) => f.symbol);
  const erc20Assets = assets.filter((a) => a.isERC20).map((f) => f.symbol);
  const availableAssets = [...topAssets];

  if (!isEmpty(erc20Assets)) {
    availableAssets.push('ERC-20 Assets');
  }

  return availableAssets.join(', ');
};

export const normalizeData = (data = []) => {
  if (isPlainObject(data)) {
    const { t, ...rest } = data;

    return [t, rest];
  }

  return data.map((item) => {
    const { t, ...rest } = item;
    const x = t * 1000;

    if (has(item, 'v')) {
      const y = item.v;

      return [x, y];
    }

    if (has(item, 'o')) {
      const y = item.o;

      return [x, y];
    }

    if (has(item, 'volumes')) {
      const y = item.volumes;

      return [x, y];
    }

    if (has(item, 'balances')) {
      const y = item.balances;

      return [x, y];
    }

    return [x, rest];
  });
};

export const applyMovingMath = (data = [], value, type = MOVING_AVERAGE) => {
  if (isEmpty(data)) {
    return data;
  }

  // skip if `y` is a multi-value
  const head = get(first(data), '1');
  if (isPlainObject(head)) {
    return data;
  }

  // skip nulls
  const d = data.filter((item) => item[1] !== null);

  // map values
  const xs = d.map((item) => item[0]);
  const ys = d.map((item) => item[1]);

  let res = [];

  if (type === MOVING_AVERAGE) {
    res = ma(ys, value);
  } else if (type === MOVING_MEDIAN) {
    res = mm(ys, value);
  }

  return xs.map((x, i) => [x, res[i]]);
};

export const denominateData = (data = [], currency, denomination) => {
  if (isEmpty(data)) {
    return data;
  }

  if (isEmpty(denomination)) {
    return data;
  }

  if (currency !== CURRENCY_NATIVE) {
    return data;
  }

  const head = get(first(data), '1');
  if (isPlainObject(head)) {
    return data;
  }

  const { multiplier } = denomination;

  return data.map((item) => {
    const [x, y] = item;

    if (!y) {
      return item;
    }

    return [x, y * multiplier];
  });
};

export const getErrTitle = (errMsg = '', isUserProfessional) => {
  if (errMsg.includes(STATUS_TOO_MANY_REQUESTS)) {
    if (isUserProfessional) {
      return Content.errorRateLimit;
    }

    return Content.errorRateLimitUpgrade;
  }

  return Content.error;
};

export const affixForExchange = (isActive, value) => {
  if (!isActive) {
    return '';
  }

  if (value === DEFAULT_EXCHANGE) {
    return '- All Exchanges';
  }

  return `- ${upperFirst(value)}`;
};

export const affixForMiner = (isActive, value) => {
  if (!isActive) {
    return '';
  }

  if (value === DEFAULT_MINER) {
    return '- All Miners';
  }

  return `- ${upperFirst(value)}`;
};

export const affixForMovingMath = (isActive, value, resolution, calcName) => {
  if (!isActive) {
    return '';
  }

  if (value === 0) {
    return '';
  }

  const unit = get(
    {
      [RESOLUTION_1H]: 'h',
      [RESOLUTION_10MIN]: 'min',
      [RESOLUTION_24H]: 'd',
      [RESOLUTION_1W]: 'w',
      [RESOLUTION_1M]: 'm',
    },
    resolution,
    '',
  );

  return `(${value}${unit} ${calcName})`;
};

export const affixForCurrency = (isActive, value, valueLabel, formatter) => {
  if (!isActive) {
    if (formatter === FORMATTER_CURRENCY_USD) {
      return '[USD]';
    }

    if (formatter === FORMATTER_CURRENCY) {
      return `[${valueLabel}]`;
    }

    return '';
  }

  if (value === CURRENCY_USD) {
    return '[USD]';
  }

  return `[${valueLabel}]`;
};

export const getResolutionUnit = (resolution) => {
  if (resolution === RESOLUTION_10MIN) {
    return 'Minutes';
  }

  if (resolution === RESOLUTION_1H) {
    return 'Hours';
  }

  if (resolution === RESOLUTION_1W) {
    return 'Weeks';
  }

  if (resolution === RESOLUTION_1M) {
    return 'Months';
  }

  return 'Days';
};

export const applyMultilineMovingMath = (data = {}, value, type = MOVING_AVERAGE) => {
  const mathedData = {};

  if (!isPlainObject(data)) {
    return data;
  }

  Object.keys(data).forEach((key) => {
    mathedData[key] = applyMovingMath(data[key], value, type);
  });

  return mathedData;
};
